import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { Shield, Copy, Download, RefreshCw, AlertTriangle } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

interface TwoFactorStatus {
  twoFactorEnabled: boolean;
  twoFactorVerified: boolean;
  requiresSetup: boolean;
  requiresVerification: boolean;
  lastVerification?: string;
}

interface SetupData {
  qrCodeUrl: string;
  backupCodes: string[];
  message: string;
}

export function TwoFactorSetup() {
  const [setupData, setSetupData] = useState<SetupData | null>(null);
  const [verificationToken, setVerificationToken] = useState("");
  const [isSettingUp, setIsSettingUp] = useState(false);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Get 2FA status
  const { data: status, isLoading } = useQuery<TwoFactorStatus>({
    queryKey: ['/api/admin/2fa/status'],
    retry: 2,
  });

  // Setup 2FA mutation
  const setupMutation = useMutation({
    mutationFn: async () => {
      console.log('[2FA SETUP] Starting setup mutation');
      
      // Get auth headers manually to debug
      const authHeaders: Record<string, string> = {};
      
      if (supabase) {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) {
          console.error('[2FA SETUP] Failed to get session:', error);
        } else if (session?.access_token) {
          console.log('[2FA SETUP] Adding Bearer token to request');
          authHeaders.Authorization = `Bearer ${session.access_token}`;
        } else {
          console.log('[2FA SETUP] No session or access token available');
        }
      } else {
        console.log('[2FA SETUP] Supabase client not available');
      }
      
      console.log('[2FA SETUP] Auth headers:', Object.keys(authHeaders));
      
      const response = await fetch('/api/admin/2fa/setup', {
        method: 'POST',
        headers: authHeaders,
        credentials: 'include',
      });

      if (!response.ok) {
        const error = await response.json();
        console.error('[2FA SETUP] Setup failed:', error);
        throw new Error(error.error || 'Failed to setup 2FA');
      }

      return response.json();
    },
    onSuccess: (data: SetupData) => {
      setSetupData(data);
      setIsSettingUp(true);
      toast({
        title: "2FA Setup Started",
        description: "Scan the QR code with your authenticator app",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Setup Failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Verify setup mutation
  const verifySetupMutation = useMutation({
    mutationFn: async (token: string) => {
      console.log('[2FA VERIFY] Starting verification with token:', token);
      
      // Get auth headers manually to debug
      const authHeaders: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      
      if (supabase) {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) {
          console.error('[2FA VERIFY] Failed to get session:', error);
        } else if (session?.access_token) {
          console.log('[2FA VERIFY] Adding Bearer token to request');
          authHeaders.Authorization = `Bearer ${session.access_token}`;
        } else {
          console.log('[2FA VERIFY] No session or access token available');
        }
      } else {
        console.log('[2FA VERIFY] Supabase client not available');
      }
      
      console.log('[2FA VERIFY] Auth headers:', Object.keys(authHeaders));
      
      const response = await fetch('/api/admin/2fa/verify-setup', {
        method: 'POST',
        headers: authHeaders,
        credentials: 'include',
        body: JSON.stringify({ token }),
      });

      if (!response.ok) {
        const error = await response.json();
        console.error('[2FA VERIFY] Verification failed:', error);
        throw new Error(error.error || 'Verification failed');
      }

      const result = await response.json();
      console.log('[2FA VERIFY] Verification successful:', result);
      return result;
    },
    onSuccess: () => {
      toast({
        title: "2FA Enabled Successfully",
        description: "Your admin account is now secured with 2FA",
      });
      setIsSettingUp(false);
      setSetupData(null);
      setVerificationToken("");
      queryClient.invalidateQueries({ queryKey: ['/api/admin/2fa/status'] });
    },
    onError: (error: Error) => {
      toast({
        title: "Verification Failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Regenerate backup codes mutation
  const regenerateCodesMutation = useMutation({
    mutationFn: async () => {
      console.log('[2FA REGENERATE] Starting backup codes regeneration');
      
      // Get auth headers manually
      const authHeaders: Record<string, string> = {};
      
      if (supabase) {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) {
          console.error('[2FA REGENERATE] Failed to get session:', error);
        } else if (session?.access_token) {
          console.log('[2FA REGENERATE] Adding Bearer token to request');
          authHeaders.Authorization = `Bearer ${session.access_token}`;
        } else {
          console.log('[2FA REGENERATE] No session or access token available');
        }
      } else {
        console.log('[2FA REGENERATE] Supabase client not available');
      }
      
      const response = await fetch('/api/admin/2fa/regenerate-backup-codes', {
        method: 'POST',
        headers: authHeaders,
        credentials: 'include',
      });

      if (!response.ok) {
        const error = await response.json();
        console.error('[2FA REGENERATE] Regeneration failed:', error);
        throw new Error(error.error || 'Failed to regenerate backup codes');
      }

      return response.json();
    },
    onSuccess: (data) => {
      setSetupData({ ...setupData, backupCodes: data.backupCodes } as SetupData);
      setShowBackupCodes(true);
      toast({
        title: "Backup Codes Regenerated",
        description: "Save your new backup codes securely",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to Regenerate",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleSetup = () => {
    setupMutation.mutate();
  };

  const handleVerifySetup = () => {
    if (!verificationToken.trim()) {
      toast({
        title: "Token Required",
        description: "Please enter the verification code from your authenticator app",
        variant: "destructive",
      });
      return;
    }

    verifySetupMutation.mutate(verificationToken);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied",
      description: "Copied to clipboard",
    });
  };

  const downloadBackupCodes = () => {
    if (!setupData?.backupCodes) return;
    
    const content = `PrizeDrop Admin 2FA Backup Codes\nGenerated: ${new Date().toLocaleString()}\n\n${setupData.backupCodes.join('\n')}\n\nKeep these codes safe - each can only be used once.`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'prizedrop-2fa-backup-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center space-x-2">
            <RefreshCw className="h-4 w-4 animate-spin" />
            <span>Loading 2FA status...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (status?.twoFactorEnabled && status?.twoFactorVerified) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Shield className="h-5 w-5 text-green-600" />
            <span>Two-Factor Authentication</span>
            <Badge variant="secondary" className="bg-green-100 text-green-800">
              Enabled
            </Badge>
          </CardTitle>
          <CardDescription>
            Your admin account is protected with 2FA. Last verification: {" "}
            {status.lastVerification 
              ? new Date(status.lastVerification).toLocaleString()
              : "Never"
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Button
              onClick={() => regenerateCodesMutation.mutate()}
              disabled={regenerateCodesMutation.isPending}
              variant="outline"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Regenerate Backup Codes
            </Button>
            
            {showBackupCodes && setupData?.backupCodes && (
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  <div className="space-y-2">
                    <p className="font-medium">Your new backup codes:</p>
                    <div className="grid grid-cols-2 gap-2 font-mono text-sm">
                      {setupData.backupCodes.map((code, index) => (
                        <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                          <span>{code}</span>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => copyToClipboard(code)}
                          >
                            <Copy className="h-3 w-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                    <div className="flex space-x-2">
                      <Button size="sm" onClick={downloadBackupCodes}>
                        <Download className="h-3 w-3 mr-1" />
                        Download
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setShowBackupCodes(false)}>
                        Hide
                      </Button>
                    </div>
                  </div>
                </AlertDescription>
              </Alert>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <Shield className="h-5 w-5 text-orange-600" />
          <span>Two-Factor Authentication Setup</span>
          <Badge variant="outline" className="bg-orange-100 text-orange-800">
            Required
          </Badge>
        </CardTitle>
        <CardDescription>
          2FA is required for all admin users. Set up an authenticator app to secure your account.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {!isSettingUp ? (
          <div className="space-y-4">
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                Two-factor authentication is mandatory for admin access. You'll need an authenticator app like Google Authenticator, Authy, or similar.
              </AlertDescription>
            </Alert>
            
            <Button 
              onClick={handleSetup}
              disabled={setupMutation.isPending}
              className="w-full"
            >
              {setupMutation.isPending && <RefreshCw className="h-4 w-4 mr-2 animate-spin" />}
              Start 2FA Setup
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            <Tabs defaultValue="qr" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="qr">QR Code</TabsTrigger>
                <TabsTrigger value="backup">Backup Codes</TabsTrigger>
              </TabsList>
              
              <TabsContent value="qr" className="space-y-4">
                <div className="text-center space-y-4">
                  {setupData?.qrCodeUrl && (
                    <div className="flex justify-center">
                      <img 
                        src={setupData.qrCodeUrl} 
                        alt="2FA QR Code" 
                        className="border rounded-lg"
                      />
                    </div>
                  )}
                  
                  <div className="space-y-2">
                    <Label htmlFor="verification-token">
                      Enter the 6-digit code from your authenticator app:
                    </Label>
                    <Input
                      id="verification-token"
                      type="text"
                      placeholder="123456"
                      value={verificationToken}
                      onChange={(e) => setVerificationToken(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      className="text-center font-mono text-lg"
                      maxLength={6}
                    />
                  </div>
                  
                  <Button 
                    onClick={handleVerifySetup}
                    disabled={verifySetupMutation.isPending || verificationToken.length !== 6}
                    className="w-full"
                  >
                    {verifySetupMutation.isPending && <RefreshCw className="h-4 w-4 mr-2 animate-spin" />}
                    Enable 2FA
                  </Button>
                </div>
              </TabsContent>
              
              <TabsContent value="backup" className="space-y-4">
                <Alert>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    Save these backup codes securely. Each code can only be used once if you lose access to your authenticator app.
                  </AlertDescription>
                </Alert>
                
                {setupData?.backupCodes && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-2 font-mono text-sm">
                      {setupData.backupCodes.map((code, index) => (
                        <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                          <span>{code}</span>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => copyToClipboard(code)}
                          >
                            <Copy className="h-3 w-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                    
                    <Button onClick={downloadBackupCodes} variant="outline" className="w-full">
                      <Download className="h-4 w-4 mr-2" />
                      Download Backup Codes
                    </Button>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        )}
      </CardContent>
    </Card>
  );
}